import axios from "axios";

export const NETWORK_STATUS_CACHE_TTL_MS = 60 * 1000;

const networkStatusCache = new Map();
const networkStatusRequests = new Map();

export const getNetworkStatusCacheKey = (serial) =>
  serial === undefined || serial === null ? "" : String(serial);

export const readNetworkStatusCache = (serial) => {
  const key = getNetworkStatusCacheKey(serial);
  if (!key) return null;
  const entry = networkStatusCache.get(key);
  if (!entry) return null;
  return {
    ...entry,
    fresh: Date.now() - entry.fetchedAt < NETWORK_STATUS_CACHE_TTL_MS,
  };
};

export const writeNetworkStatusCache = (serial, entry) => {
  const key = getNetworkStatusCacheKey(serial);
  if (!key) return;
  networkStatusCache.set(key, {
    data: entry.data || null,
    unavailable: Boolean(entry.unavailable),
    fetchedAt: Date.now(),
  });
};

export const clearNetworkStatusCacheForTests = () => {
  networkStatusCache.clear();
  networkStatusRequests.clear();
};

export const fetchNetworkStatusWithCache = async ({
  serial,
  headers,
  force = false,
} = {}) => {
  const key = getNetworkStatusCacheKey(serial);
  if (!key) {
    return { data: null, unavailable: false, cached: false };
  }

  if (!force) {
    const cached = readNetworkStatusCache(key);
    if (cached?.fresh && (cached.data || cached.unavailable)) {
      return {
        data: cached.data || null,
        unavailable: Boolean(cached.unavailable),
        cached: true,
      };
    }

    const pendingRequest = networkStatusRequests.get(key);
    if (pendingRequest) {
      return pendingRequest;
    }
  }

  const encodedSerial = encodeURIComponent(key);
  const request = axios
    .get(`/v1/devices/${encodedSerial}/network/status`, { headers })
    .then(({ data }) => {
      const nextStatus = data?.data || {};
      writeNetworkStatusCache(key, {
        data: nextStatus,
        unavailable: false,
      });
      return {
        data: nextStatus,
        unavailable: false,
        cached: false,
      };
    })
    .finally(() => {
      if (networkStatusRequests.get(key) === request) {
        networkStatusRequests.delete(key);
      }
    });

  networkStatusRequests.set(key, request);
  return request;
};
